<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { LINKS } from '@Constants/links.constants';
import { isCashToCloseSendType, isEmdSendType } from '@Enums/sendType.enum';
import { isSameDayWindowMissed } from '@Enums/ctcEligibilityStatus.enum';

import { GuidedShowcaseLayout, useCurrency, useDateFormatter } from '@Shared';
import { useFlowSteps } from '@Use/useFlowSteps';

// Assets
import houseWithClockIllustration from '@Assets/images/house-with-clock.svg';
import certifidBrandingLogo from '@Assets/images/certifid-branding.svg';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const { advanceFlow, goBackInFlow } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const requestDetailsStore = useRequestDetailsStore();
const { formatCurrency } = useCurrency();
const { formatLocalDateTime } = useDateFormatter();

// Page state
const isRequestDetailsLoading = ref<boolean>(true);
const isNavigating = ref(false);

// Computed (all backed by the real BFF payload via requestDetailsStore).
// closingDate arrives pre-formatted from the BFF; guaranteedArrivalUtc, closingDateTimeUtc, and
// sameDaySubmissionDeadline are raw ISO 8601 UTC timestamps and are formatted client-side into
// the viewer's local timezone. The latter two are only populated when sameDayDigitalAllowed is true.
const requestDetails = computed(() => requestDetailsStore.requestDetails);

const brandName = computed(() => requestDetails.value?.brandName ?? '');

// DS-3309: branch purpose copy on the request's send type so an earnest money
// deposit request reads as EMD rather than cash-to-close / closing payment.
const isEmd = computed(() => isEmdSendType(requestDetails.value?.sendType));
const purposeBadge = computed(() => (isEmd.value ? 'Earnest Money Deposit' : 'Cash to Close'));

const paymentAmount = computed(() =>
    requestDetails.value?.transactionValue != null
        ? formatCurrency(requestDetails.value.transactionValue)
        : '',
);

const propertyAddress = computed(() => requestDetails.value?.property ?? '');
const closingDateDisplay = computed(() => requestDetails.value?.closingDate ?? '');
const hasClosingDate = computed(() => closingDateDisplay.value.trim() !== '');
const guaranteedArrival = computed(() =>
    formatLocalDateTime(requestDetails.value?.guaranteedArrivalUtc),
);
const isEligibleForDigital = computed(() => requestDetails.value?.isEligibleForDigital ?? false);
const isCashToClose = computed(() => isCashToCloseSendType(requestDetails.value?.sendType));
const showGuaranteedArrivalBanner = computed(
    () => isCashToClose.value && isEligibleForDigital.value && guaranteedArrival.value !== null,
);
const guaranteedArrivalDetail = computed(() => {
    const arrival = guaranteedArrival.value;
    return arrival ? arrival.date : '';
});

// PAY-3511: same-day closing instant and submission deadline, when available.
const isSameDayDigitalAllowed = computed(() => requestDetails.value?.sameDayDigitalAllowed ?? false);

const closingDateTime = computed(() =>
    formatLocalDateTime(requestDetails.value?.closingDateTimeUtc),
);
const showClosingDateTime = computed(
    () => isSameDayDigitalAllowed.value && closingDateTime.value !== null,
);
const closingDateTimeDetail = computed(() => {
    const closing = closingDateTime.value;
    return closing ? `${closing.date} at ${closing.time}` : '';
});

// A single "Closing" row: prefer the precise closing instant (date + time) when the
// request carries one, otherwise fall back to the date-only string — never both.
const closingDisplay = computed(() =>
    (showClosingDateTime.value ? closingDateTimeDetail.value : closingDateDisplay.value),
);
const hasClosingRow = computed(() => showClosingDateTime.value || hasClosingDate.value);

const submissionDeadline = computed(() =>
    formatLocalDateTime(requestDetails.value?.sameDaySubmissionDeadline),
);
// Gate submission deadline on the window NOT being missed. The BFF populates sameDaySubmissionDeadline
// on every same-day branch, including when the window has already passed (ctcEligibilityStatus: 'SameDayWindowMissed'),
// so we must explicitly check that the window is still open to avoid instructing the buyer to meet a deadline in the past.
const showSubmissionDeadline = computed(
    () =>
        isSameDayDigitalAllowed.value &&
        submissionDeadline.value !== null &&
        !isSameDayWindowMissed(requestDetails.value?.ctcEligibilityStatus),
);
const submissionDeadlineDetail = computed(() => {
    const deadline = submissionDeadline.value;
    return deadline ? `${deadline.date} at ${deadline.time}` : '';
});

// Actions
const continueToNextStep = () => {
    isNavigating.value = true;
    advanceFlow();
};

// Lifecycle
onMounted(async () => {
    isRequestDetailsLoading.value = true;
    await requestDetailsStore.fetchRequestDetails();
    isRequestDetailsLoading.value = false;
});
</script>

<template>
    <guided-showcase-layout
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="isRequestDetailsLoading">
            <app-page-loader />
        </template>

        <template v-else>
            <!-- House illustration — mobile only; desktop sees it in the #branded panel -->
            <div class="payment-request-overview__mobile-graphic">
                <img :src="houseWithClockIllustration" alt="" />
            </div>

            <!-- Purpose badge -->
            <span class="payment-request-overview__badge">{{ purposeBadge }}</span>

            <!-- Prominent payment amount -->
            <h1 class="payment-request-overview__amount">{{ paymentAmount }}</h1>

            <!-- Body copy -->
            <p class="payment-request-overview__body">
                {{ brandName }} has requested your
                <template v-if="isEmd">earnest money deposit</template>
                <template v-else>closing payment</template>
                for:
            </p>

            <!-- Property + closing date container (closing row only when the request has a date) -->
            <div class="payment-request-overview__details">
                <div class="payment-request-overview__detail-row">
                    <app-icon icon="cd-ic-home" class="payment-request-overview__detail-icon" />
                    <div class="payment-request-overview__detail-text">
                        <span class="payment-request-overview__detail-label">Property</span>
                        <span class="payment-request-overview__detail-value">{{ propertyAddress }}</span>
                    </div>
                </div>
                <div v-if="hasClosingRow" class="payment-request-overview__detail-row">
                    <app-icon icon="cd-ic-calendar" class="payment-request-overview__detail-icon" />
                    <div class="payment-request-overview__detail-text">
                        <span class="payment-request-overview__detail-label">Closing</span>
                        <span class="payment-request-overview__detail-value">{{ closingDisplay }}</span>
                    </div>
                </div>
            </div>

            <!-- Guaranteed arrival banner (cash-to-close only, eligible buyers only, and only
                 when the BFF returned a value) -->
            <div
                v-if="showGuaranteedArrivalBanner"
                class="payment-request-overview__arrival-banner"
            >
                <div class="payment-request-overview__arrival-icon">
                    <app-icon icon="cd-ic-calendar-check" class="payment-request-overview__arrival-icon-glyph" />
                </div>
                <div class="payment-request-overview__arrival-text">
                    <span class="payment-request-overview__arrival-title">Guaranteed arrival</span>
                    <span class="payment-request-overview__arrival-detail">{{ guaranteedArrivalDetail }}</span>
                </div>
            </div>

            <!-- PAY-3511: same-day submission deadline (when available) -->
            <div
                v-if="showSubmissionDeadline"
                class="payment-request-overview__same-day-row"
            >
                <div class="payment-request-overview__same-day-icon">
                    <app-icon icon="cd-ic-calendar-check" class="payment-request-overview__same-day-icon-glyph" />
                </div>
                <div class="payment-request-overview__same-day-text">
                    <span class="payment-request-overview__same-day-label">Submit by</span>
                    <span class="payment-request-overview__same-day-deadline-value">{{ submissionDeadlineDetail }}</span>
                </div>
            </div>

            <!-- CTA -->
            <app-button
                expanded
                size="large"
                :is-loading="isNavigating"
                data-id="payment-request-overview-continue"
                @onClick="continueToNextStep"
            >
                Continue to Payment
            </app-button>

            <!-- Legal -->
            <p class="payment-request-overview__legal">
                By continuing, you agree to our
                <app-link type="external" :link-url="LINKS.CertifID.TermsOfUse">Terms of Use</app-link>
                and
                <app-link type="external" :link-url="LINKS.CertifID.PrivacyPolicy">Privacy Policy</app-link>.
            </p>

            <!-- Rendered in the content column rather than ShowcaseLayoutFooter because the footer
                 centers within the full 45rem container, misaligning it from the 480px content column. -->
            <div class="payment-request-overview__powered-by">
                <img
                    :src="certifidBrandingLogo"
                    alt=""
                    class="payment-request-overview__powered-by-logo"
                />
            </div>
        </template>

        <!-- Desktop: house illustration in the right-side branded panel -->
        <template #branded>
            <div class="payment-request-overview__branded">
                <img :src="houseWithClockIllustration" alt="" />
            </div>
        </template>
    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

// Shared placeholder styles for arrival banner and same-day rows.
%row-container {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
}

%icon-circle {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

%text-container {
    display: flex;
    flex-direction: column;
}

%text-base {
    font-size: $small-text;
    line-height: 150%;
}

%text-bold {
    @extend %text-base;
    font-weight: 700;
}

%text-regular {
    @extend %text-base;
    font-weight: 400;
}

.payment-request-overview {
    &__badge {
        align-self: flex-start;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem 0.875rem;
        border-radius: 1rem;
        background: var(--ch-primary-lightest);
        color: var(--ch-primary);
        font-size: $xs-small-text;
        font-weight: 700;
        line-height: 150%;
    }

    &__amount {
        font-size: 2rem;
        font-weight: 700;
        line-height: 1.2;
        color: var(--ch-primary-dark);
        margin: 0.75rem 0 0.5rem;
    }

    &__body {
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.5;
        color: $black-med;
        margin: 0 0 1.25rem;
    }

    &__details {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem;
        background: $gray-lightest;
        border-radius: 0.5rem;
        margin-bottom: 1rem;
    }

    &__detail-row {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
    }

    &__detail-icon {
        flex-shrink: 0;
        font-size: 1.25rem;
        color: $black-med;
    }

    &__detail-text {
        display: flex;
        flex-direction: column;
    }

    &__detail-label {
        font-size: 0.875rem;
        font-weight: 700;
        color: $black-high;
        line-height: 1.4;
    }

    &__detail-value {
        font-size: 0.875rem;
        font-weight: 400;
        color: $black-med;
        line-height: 1.4;
    }

    &__arrival-banner {
        @extend %row-container;
        background: #E8F8EF;
    }

    &__arrival-icon {
        @extend %icon-circle;
    }

    &__arrival-icon-glyph {
        font-size: 1.5rem;
        color: $dark-green;
    }

    &__arrival-text {
        @extend %text-container;
    }

    &__arrival-title {
        @extend %text-bold;
        color: $dark-green;
    }

    &__arrival-detail {
        @extend %text-regular;
        color: $dark-green;
    }

    // PAY-3511: same-day closing and submission deadline rows.
    &__same-day-row {
        @extend %row-container;
        background: $gray-lightest;
    }

    &__same-day-icon {
        @extend %icon-circle;
    }

    &__same-day-icon-glyph {
        font-size: 1.5rem;
        color: $black-med;
    }

    &__same-day-text {
        @extend %text-container;
    }

    &__same-day-label {
        @extend %text-bold;
        color: $black-high;
    }

    &__same-day-deadline-value {
        @extend %text-regular;
        color: $black-med;
    }

    &__legal {
        font-size: 0.75rem;
        color: $black-med;
        text-align: center;
        line-height: 1.5;
        margin: 1.5rem 0;
    }

    &__mobile-graphic {
        display: flex;
        justify-content: center;
        margin-bottom: 1rem;

        @include desktop {
            display: none;
        }

        img {
            max-width: 11rem;
        }
    }

    &__branded {
        display: flex;
        flex: 1 1 auto;
        align-items: center;
        justify-content: center;

        img {
            max-width: 28rem;
            width: 100%;
        }
    }

    &__powered-by {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 2rem;
        padding-bottom: 1.5rem;
    }

    &__powered-by-logo {
        width: 7.3125rem;
        height: auto;
    }
}
</style>
